/**
 * ProjectListPage - displays list of projects in the organization
 * Supports multiple view modes: grid, list, timeline, table
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import {
  Plus,
  FolderKanban,
  Archive,
  MoreHorizontal,
  Search,
  Pencil,
  Trash2,
  X,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import * as Dialog from '@radix-ui/react-dialog';
import { useProjectStore } from '@stores/projectStore';
import { useAuthStore } from '@stores/authStore';
import { useSubscriptionStore } from '@stores/subscriptionStore';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import { UpgradePrompt } from '@/components/subscription';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  GenericViewSwitcher,
  AllTasksView,
  AllFilesView,
  type GenericViewType,
} from '@/components/shared/views';
import {
  IconPicker,
  ColorPicker,
  colorOptions,
  isEmoji,
  getLucideIcon,
} from '@/components/shared/IconPicker';

// Schema for editing project (key is not editable)
const editProjectSchema = z.object({
  name: z
    .string()
    .min(1, 'Project name is required')
    .max(100, 'Project name must be less than 100 characters'),
  description: z
    .string()
    .max(500, 'Description must be less than 500 characters')
    .optional(),
  isPublic: z.boolean().default(false),
});

type EditProjectFormData = z.infer<typeof editProjectSchema>;

// Schema for creating project (includes key)
const createProjectSchema = z.object({
  name: z
    .string()
    .min(1, 'Project name is required')
    .max(100, 'Project name must be less than 100 characters'),
  key: z
    .string()
    .min(2, 'Key must be at least 2 characters')
    .max(10, 'Key must be less than 10 characters')
    .transform((val) => val.toUpperCase())
    .refine(
      (val) => /^[A-Z][A-Z0-9]*$/.test(val),
      'Key must start with a letter and contain only uppercase letters and numbers'
    ),
  description: z
    .string()
    .max(500, 'Description must be less than 500 characters')
    .optional(),
  isPublic: z.boolean().default(false),
});

type CreateProjectFormData = z.infer<typeof createProjectSchema>;

interface ProjectForEdit {
  id: string;
  name: string;
  key: string;
  description?: string;
  isPublic?: boolean;
  color?: string;
  icon?: string;
}

export default function ProjectListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrgId } = useAuthStore();
  const { projects, isLoading, error, fetchProjects, archiveProject, deleteProject, updateProject, createProject } =
    useProjectStore();
  const { usage, currentSubscription, fetchCurrentSubscription } = useSubscriptionStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [showCreateProjectDialog, setShowCreateProjectDialog] = useState(false);
  const [viewType, setViewType] = useState<GenericViewType>('grid');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const [editingProject, setEditingProject] = useState<ProjectForEdit | null>(null);

  useEffect(() => {
    if (currentOrgId) {
      fetchProjects(currentOrgId);
      fetchCurrentSubscription().catch((err) => {
        // Silently fail if subscription data unavailable
        console.warn('Failed to fetch subscription data:', err);
      });
    }
  }, [currentOrgId, fetchProjects, fetchCurrentSubscription]);

  const handleCreateProject = async (data: {
    name: string;
    key: string;
    description?: string;
    isPublic?: boolean;
    icon?: string;
    color?: string;
  }) => {
    if (!currentOrgId) return;

    const createData: {
      organizationId: string;
      name: string;
      key: string;
      description?: string;
      isPublic?: boolean;
      icon?: string;
      color?: string;
    } = {
      organizationId: currentOrgId,
      name: data.name,
      key: data.key,
    };
    if (data.description) {
      createData.description = data.description;
    }
    if (data.isPublic !== undefined) {
      createData.isPublic = data.isPublic;
    }
    if (data.icon) {
      createData.icon = data.icon;
    }
    if (data.color) {
      createData.color = data.color;
    }
    const project = await createProject(createData);
    setShowCreateProjectDialog(false);
    navigate(`/projects/${project.id}`);
  };

  const filteredProjects = projects.filter((project) => {
    // Status filter
    if (filter === 'active' && project.status !== 'active') return false;
    if (filter === 'archived' && project.status !== 'archived') return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        project.name.toLowerCase().includes(query) ||
        project.key.toLowerCase().includes(query) ||
        project.description?.toLowerCase().includes(query)
      );
    }

    return true;
  });

  if (isLoading && projects.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <LoadingSpinner size="lg" className="text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Header */}
      <header className="flex-shrink-0 border-b bg-white dark:bg-neutral-900 p-4">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FolderKanban className="w-6 h-6 text-blue-500" />
              <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">{t('project.title')}</h1>
              <span className="text-neutral-500">({filteredProjects.length})</span>
            </div>
            <button
              onClick={() => setShowCreateProjectDialog(true)}
              className={clsx(
                'flex items-center gap-2 rounded-lg px-4 py-2',
                'bg-primary-600 text-white hover:bg-primary-700',
                'transition-colors'
              )}
            >
              <Plus className="h-4 w-4" />
              {t('project.newProject')}
            </button>
          </div>

          {/* Filters and View Switcher */}
          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 flex-1">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                <input
                  type="text"
                  placeholder={t('project.searchProjects')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={clsx(
                    'w-full rounded-lg border border-neutral-300 py-2 pl-10 pr-4',
                    'bg-white text-sm text-neutral-900',
                    'dark:border-neutral-700 dark:bg-neutral-800 dark:text-white',
                    'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                  )}
                />
              </div>

              {/* Status filter */}
              <div className="flex rounded-lg border border-neutral-300 p-1 dark:border-neutral-700">
                {(['active', 'archived', 'all'] as const).map((status) => (
                  <button
                    key={status}
                    onClick={() => setFilter(status)}
                    className={clsx(
                      'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                      filter === status
                        ? 'bg-neutral-200 text-neutral-900 dark:bg-neutral-700 dark:text-white'
                        : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
                    )}
                  >
                    {t(`project.filter${status.charAt(0).toUpperCase()}${status.slice(1)}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* View Switcher */}
            <GenericViewSwitcher
              currentView={viewType}
              onViewChange={setViewType}
            />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-7xl px-6 py-8">
        {error && (
          <div className="mb-6 rounded-lg bg-red-100 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Upgrade prompt when approaching limits */}
        {usage && (
          <UpgradePrompt
            usage={usage}
            {...(currentSubscription?.plan.name && { planName: currentSubscription.plan.name })}
            className="mb-6"
          />
        )}

        {filteredProjects.length === 0 ? (
          <div className="rounded-xl border border-neutral-200 bg-white p-12 text-center dark:border-neutral-800 dark:bg-neutral-900">
            <FolderKanban className="mx-auto h-12 w-12 text-neutral-300 dark:text-neutral-600" />
            <h3 className="mt-4 text-lg font-medium text-neutral-900 dark:text-white">
              {t('project.noProjectsFound')}
            </h3>
            <p className="mt-2 text-neutral-500 dark:text-neutral-400">
              {searchQuery
                ? t('project.noProjectsSearch')
                : filter === 'archived'
                  ? t('project.noArchivedProjects')
                  : t('project.createFirst')}
            </p>
            {!searchQuery && filter !== 'archived' && (
              <button
                onClick={() => setShowCreateProjectDialog(true)}
                className={clsx(
                  'mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2',
                  'bg-primary-600 text-white hover:bg-primary-700'
                )}
              >
                <Plus className="h-4 w-4" />
                {t('project.create')}
              </button>
            )}
          </div>
        ) : viewType === 'grid' ? (
          // Grid View (original card layout)
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onArchive={() => archiveProject(project.id)}
                onDelete={() => {
                  setDeletingProjectId(project.id);
                  setShowDeleteConfirm(true);
                }}
                onEdit={() => setEditingProject(project)}
              />
            ))}
          </div>
        ) : viewType === 'files' ? (
          // Files View - Show ALL files across all boards
          <AllFilesView searchQuery={searchQuery} />
        ) : (
          // List/Timeline/Table Views - Show ALL tasks across all boards
          <AllTasksView
            viewMode={viewType === 'list' ? 'list' : viewType === 'timeline' ? 'timeline' : 'table'}
            searchQuery={searchQuery}
          />
        )}

        {/* Delete project confirmation dialog */}
        <ConfirmDialog
          open={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDeletingProjectId(null);
          }}
          onConfirm={() => {
            if (deletingProjectId) {
              deleteProject(deletingProjectId);
              setShowDeleteConfirm(false);
              setDeletingProjectId(null);
            }
          }}
          title={t('project.deleteProjectTitle')}
          description={t('project.deleteProjectDescription')}
          confirmLabel={t('common.delete')}
          variant="danger"
        />

        {/* Edit project dialog */}
        <EditProjectDialog
          project={editingProject}
          open={!!editingProject}
          onClose={() => setEditingProject(null)}
          onSave={async (data) => {
            if (editingProject) {
              await updateProject(editingProject.id, data);
              setEditingProject(null);
            }
          }}
        />

        {/* Create project dialog */}
        <CreateProjectDialog
          open={showCreateProjectDialog}
          onClose={() => setShowCreateProjectDialog(false)}
          onCreate={handleCreateProject}
        />
      </main>
    </div>
  );
}

interface ProjectCardProps {
  project: {
    id: string;
    name: string;
    key: string;
    description?: string;
    status: string;
    iconColor?: string;
    color?: string;
    icon?: string;
    isPublic?: boolean;
    memberCount?: number;
    boardCount?: number;
    taskCount?: number;
  };
  onArchive: () => void;
  onDelete: () => void;
  onEdit: () => void;
}

function ProjectCard({ project, onArchive, onDelete, onEdit }: ProjectCardProps) {
  const { t } = useTranslation();
  const iconValue = project.icon || 'folder-kanban';
  const isEmojiIcon = isEmoji(iconValue);
  const LucideIcon = !isEmojiIcon ? getLucideIcon(iconValue) || FolderKanban : null;

  return (
    <Link
      to={`/projects/${project.id}`}
      className={clsx(
        'group relative block rounded-xl border border-neutral-200 bg-white p-5',
        'transition-shadow hover:shadow-lg',
        'dark:border-neutral-800 dark:bg-neutral-900',
        project.status === 'archived' && 'opacity-60'
      )}
    >
      {/* Icon */}
      <div
        className={clsx(
          'flex h-10 w-10 items-center justify-center rounded-lg text-white',
          project.color ? '' : 'bg-primary-600'
        )}
        style={project.color ? { backgroundColor: project.color } : undefined}
      >
        {isEmojiIcon ? (
          <span className="text-xl">{iconValue}</span>
        ) : LucideIcon ? (
          <LucideIcon className="h-5 w-5" />
        ) : (
          <FolderKanban className="h-5 w-5" />
        )}
      </div>

      {/* Content */}
      <div className="mt-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-neutral-900 dark:text-white">
            {project.name}
          </h3>
          <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-xs font-medium text-neutral-500 dark:bg-neutral-800">
            {project.key}
          </span>
        </div>
        {project.description && (
          <p className="mt-1 line-clamp-2 text-sm text-neutral-500 dark:text-neutral-400">
            {project.description}
          </p>
        )}
        {/* Stats */}
        <div className="mt-3 flex items-center gap-3 text-sm text-neutral-500 dark:text-neutral-400">
          <span>{t('project.boardsCount', { count: project.boardCount ?? 0 })}</span>
          <span className="text-neutral-300 dark:text-neutral-600">|</span>
          <span>{t('project.tasksCount', { count: project.taskCount ?? 0 })}</span>
          {project.memberCount !== undefined && project.memberCount > 0 && (
            <>
              <span className="text-neutral-300 dark:text-neutral-600">|</span>
              <span>{t('project.membersCount', { count: project.memberCount })}</span>
            </>
          )}
        </div>
      </div>

      {/* Status badge */}
      {project.status === 'archived' && (
        <div className="absolute right-3 top-3">
          <span className="flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400">
            <Archive className="h-3 w-3" />
            {t('project.archived')}
          </span>
        </div>
      )}

      {/* Actions menu */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            onClick={(e) => e.preventDefault()}
            className={clsx(
              'absolute right-3 top-3 rounded p-1',
              'text-neutral-400 opacity-0 transition-opacity',
              'hover:bg-neutral-100 hover:text-neutral-600',
              'group-hover:opacity-100',
              'dark:hover:bg-neutral-800 dark:hover:text-neutral-300',
              project.status === 'archived' && 'top-10'
            )}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={clsx(
              'z-50 min-w-[160px] rounded-lg border border-neutral-200 bg-white p-1 shadow-lg',
              'dark:border-neutral-700 dark:bg-neutral-800'
            )}
            align="end"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenu.Item
              onSelect={onEdit}
              className={clsx(
                'flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm',
                'text-neutral-700 hover:bg-neutral-100',
                'dark:text-neutral-300 dark:hover:bg-neutral-700'
              )}
            >
              <Pencil className="h-4 w-4" />
              {t('common.edit')}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={onArchive}
              className={clsx(
                'flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm',
                'text-neutral-700 hover:bg-neutral-100',
                'dark:text-neutral-300 dark:hover:bg-neutral-700'
              )}
            >
              <Archive className="h-4 w-4" />
              {project.status === 'archived' ? t('project.restore') : t('project.archive')}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={onDelete}
              className={clsx(
                'flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm',
                'text-red-600 hover:bg-red-50',
                'dark:text-red-400 dark:hover:bg-red-900/20'
              )}
            >
              <Trash2 className="h-4 w-4" />
              {t('common.delete')}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </Link>
  );
}

// Edit Project Dialog Component
interface EditProjectDialogProps {
  project: ProjectForEdit | null;
  open: boolean;
  onClose: () => void;
  onSave: (data: { name: string; description?: string; isPublic?: boolean; color?: string; icon?: string }) => Promise<void>;
}

// Create Project Dialog Component
interface CreateProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    name: string;
    key: string;
    description?: string;
    isPublic?: boolean;
    icon?: string;
    color?: string;
  }) => Promise<void>;
}

function CreateProjectDialog({ open, onClose, onCreate }: CreateProjectDialogProps) {
  const { t } = useTranslation();
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
  const [selectedIcon, setSelectedIcon] = useState<string>('folder-kanban');
  const [isCreating, setIsCreating] = useState(false);
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateProjectFormData>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      name: '',
      key: '',
      description: '',
      isPublic: false,
    },
  });

  const nameValue = watch('name');

  // Auto-generate key from name (if not manually edited)
  useEffect(() => {
    if (!keyManuallyEdited && nameValue) {
      const generatedKey = nameValue
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 6);
      if (generatedKey) {
        setValue('key', generatedKey);
      }
    }
  }, [nameValue, keyManuallyEdited, setValue]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset({
        name: '',
        key: '',
        description: '',
        isPublic: false,
      });
      setSelectedColor(colorOptions[0]);
      setSelectedIcon('folder-kanban');
      setKeyManuallyEdited(false);
    }
  }, [open, reset]);

  const onSubmit = async (data: CreateProjectFormData) => {
    setIsCreating(true);
    try {
      const createData: {
        name: string;
        key: string;
        description?: string;
        isPublic?: boolean;
        icon?: string;
        color?: string;
      } = {
        name: data.name,
        key: data.key,
      };
      if (selectedColor) {
        createData.color = selectedColor;
      }
      if (selectedIcon) {
        createData.icon = selectedIcon;
      }
      if (data.description) {
        createData.description = data.description;
      }
      if (data.isPublic !== undefined) {
        createData.isPublic = data.isPublic;
      }
      await onCreate(createData);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-full max-w-xl rounded-xl bg-white p-6 shadow-xl',
            'dark:bg-neutral-900 dark:border dark:border-neutral-800',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          )}
        >
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('project.createNewProject')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={clsx(
                  'rounded-lg p-1.5',
                  'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600',
                  'dark:hover:bg-neutral-800 dark:hover:text-neutral-300'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-5">
            {/* Icon Picker */}
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.projectIcon')}
              </p>
              <IconPicker
                value={selectedIcon}
                onChange={setSelectedIcon}
                color={selectedColor}
              />
            </div>

            {/* Color Picker */}
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.iconColor')}
              </p>
              <ColorPicker value={selectedColor} onChange={setSelectedColor} />
            </div>

            {/* Project name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.projectNameRequired')}
              </label>
              <input
                {...register('name')}
                type="text"
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  errors.name
                    ? 'border-red-500'
                    : 'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('project.projectNamePlaceholder')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Project key (editable) */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.projectKeyRequired')}
              </label>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {t('project.projectKeyHint')}
              </p>
              <input
                {...register('key', {
                  onChange: () => setKeyManuallyEdited(true),
                })}
                type="text"
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5 uppercase',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  errors.key
                    ? 'border-red-500'
                    : 'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('project.projectKeyPlaceholder')}
              />
              {errors.key && (
                <p className="mt-1 text-sm text-red-500">{errors.key.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.description')}
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('project.projectDescPlaceholder')}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
              )}
            </div>

            {/* Visibility */}
            <div>
              <label className="flex items-center gap-3">
                <input
                  {...register('isPublic')}
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {t('project.publicProject')}
                  </span>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {t('project.publicProjectDescription')}
                  </p>
                </div>
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
              <button
                type="button"
                onClick={onClose}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'text-neutral-700 hover:bg-neutral-100',
                  'dark:text-neutral-300 dark:hover:bg-neutral-800'
                )}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isCreating}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'bg-primary-600 text-white hover:bg-primary-700',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isCreating ? t('common.creating') : t('project.create')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function EditProjectDialog({ project, open, onClose, onSave }: EditProjectDialogProps) {
  const { t } = useTranslation();
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
  const [selectedIcon, setSelectedIcon] = useState<string>('folder-kanban');
  const [isSaving, setIsSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditProjectFormData>({
    resolver: zodResolver(editProjectSchema),
  });

  // Reset form when project changes
  useEffect(() => {
    if (project) {
      reset({
        name: project.name,
        description: project.description || '',
        isPublic: project.isPublic || false,
      });
      setSelectedColor(project.color || colorOptions[0]);
      setSelectedIcon(project.icon || 'folder-kanban');
    }
  }, [project, reset]);

  const onSubmit = async (data: EditProjectFormData) => {
    setIsSaving(true);
    try {
      const saveData: { name: string; description?: string; isPublic?: boolean; color?: string; icon?: string } = {
        name: data.name,
      };
      if (selectedColor) {
        saveData.color = selectedColor;
      }
      if (selectedIcon) {
        saveData.icon = selectedIcon;
      }
      if (data.description) {
        saveData.description = data.description;
      }
      if (data.isPublic !== undefined) {
        saveData.isPublic = data.isPublic;
      }
      await onSave(saveData);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-full max-w-xl rounded-xl bg-white p-6 shadow-xl',
            'dark:bg-neutral-900 dark:border dark:border-neutral-800',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
            'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]'
          )}
        >
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('project.editProject')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={clsx(
                  'rounded-lg p-1.5',
                  'text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600',
                  'dark:hover:bg-neutral-800 dark:hover:text-neutral-300'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-5">
            {/* Icon Picker */}
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.projectIcon')}
              </p>
              <IconPicker
                value={selectedIcon}
                onChange={setSelectedIcon}
                color={selectedColor}
              />
            </div>

            {/* Color Picker */}
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.iconColor')}
              </p>
              <ColorPicker value={selectedColor} onChange={setSelectedColor} />
            </div>

            {/* Project name */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.projectNameRequired')}
              </label>
              <input
                {...register('name')}
                type="text"
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  errors.name
                    ? 'border-red-500'
                    : 'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('project.projectNamePlaceholder')}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Project key (read-only) */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.key')}
              </label>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {t('project.projectKeyCannotChange')}
              </p>
              <input
                type="text"
                value={project?.key || ''}
                readOnly
                disabled
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5 uppercase',
                  'bg-neutral-100 text-neutral-500 dark:bg-neutral-800/50 dark:text-neutral-500',
                  'border-neutral-300 dark:border-neutral-700',
                  'cursor-not-allowed'
                )}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('project.description')}
              </label>
              <textarea
                {...register('description')}
                rows={3}
                className={clsx(
                  'mt-1 w-full rounded-lg border px-4 py-2.5',
                  'bg-white text-neutral-900 dark:bg-neutral-800 dark:text-white',
                  'border-neutral-300 dark:border-neutral-700',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500'
                )}
                placeholder={t('project.projectDescPlaceholder')}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>
              )}
            </div>

            {/* Visibility */}
            <div>
              <label className="flex items-center gap-3">
                <input
                  {...register('isPublic')}
                  type="checkbox"
                  className="h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    {t('project.publicProject')}
                  </span>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    {t('project.publicProjectDescription')}
                  </p>
                </div>
              </label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
              <button
                type="button"
                onClick={onClose}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'text-neutral-700 hover:bg-neutral-100',
                  'dark:text-neutral-300 dark:hover:bg-neutral-800'
                )}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={clsx(
                  'rounded-lg px-4 py-2 text-sm font-medium',
                  'bg-primary-600 text-white hover:bg-primary-700',
                  'disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isSaving ? t('common.saving') : t('common.saveChanges')}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
